import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Trash2, RefreshCw, Mail, CheckCircle2, Clock } from "lucide-react";

const emptyForm = {
  from_email: "", from_name: "", reply_to: "", nickname: "",
  address: "", address2: "", city: "", state: "", zip: "", country: "United States",
};

export default function VerifiedSenders({ onPickSender }) {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sendgrid/senders");
      setSenders(data.senders || []);
    } catch (err) {
      toast.error("Failed to load senders: " + (err?.response?.data?.detail || ""));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/sendgrid/senders", form);
      toast.success("Sender created — check inbox for confirmation email");
      setForm(emptyForm);
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const onResend = async (id) => {
    try { await api.post(`/sendgrid/senders/${id}/resend`); toast.success("Verification email resent"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const onDelete = async (id, email) => {
    if (!window.confirm(`Remove sender ${email}?`)) return;
    try { await api.delete(`/sendgrid/senders/${id}`); toast.success("Sender removed"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="cf-card p-8 mt-8" data-testid="verified-senders-section">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-2">
            <Mail size={11} /> // sendgrid verified senders
          </div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight mt-1">Sender Identities</h2>
          <p className="text-zinc-500 text-sm mt-1 max-w-xl">
            Add a sender address to SendGrid. You'll get a confirmation email — click the link to verify. Verified senders can then be used in your campaigns.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="refresh-senders-button" className="cf-btn-secondary"><RefreshCw size={12} /> Refresh</button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="add-sender-button" className="cf-btn-primary"><Plus size={14} /> Add Sender</button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-zinc-800 rounded-none text-white max-w-2xl">
              <DialogHeader><DialogTitle className="font-display uppercase tracking-tight text-2xl">Verify New Sender</DialogTitle></DialogHeader>
              <p className="text-zinc-500 text-sm mb-2">
                SendGrid requires a real mailing address (CAN-SPAM compliance). The from-email will receive a confirmation link.
              </p>
              <form onSubmit={onCreate} className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">From email</label>
                    <input data-testid="sender-from-email" type="email" required value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} className="cf-input" placeholder="hello@yourcompany.com" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">From name</label>
                    <input data-testid="sender-from-name" required value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="cf-input" placeholder="Jane Doe" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Reply-to (optional)</label>
                    <input data-testid="sender-reply-to" type="email" value={form.reply_to} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="cf-input" placeholder="Defaults to from email" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Nickname (optional)</label>
                    <input data-testid="sender-nickname" value={form.nickname} onChange={(e) => setForm({ ...form, nickname: e.target.value })} className="cf-input" placeholder="Defaults to from email" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Street address</label>
                  <input data-testid="sender-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="cf-input" placeholder="123 Main St" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">City</label>
                    <input data-testid="sender-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="cf-input" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">State</label>
                    <input data-testid="sender-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="cf-input" />
                  </div>
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Zip / postal</label>
                    <input data-testid="sender-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="cf-input" />
                  </div>
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 block mb-2">Country</label>
                  <input data-testid="sender-country" required value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="cf-input" placeholder="United States" />
                </div>
                <DialogFooter className="pt-2">
                  <button type="submit" disabled={busy} data-testid="submit-sender" className="cf-btn-primary w-full justify-center">
                    {busy ? "Sending verification..." : "Create + Send Verification"}
                  </button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-600 text-center py-8">// loading senders...</div>
      ) : senders.length === 0 ? (
        <div className="font-mono text-xs uppercase tracking-widest text-zinc-600 text-center py-8 border border-dashed border-zinc-800">
          // no senders yet — add your first to start sending real emails
        </div>
      ) : (
        <div className="border border-zinc-800 divide-y divide-zinc-800">
          {senders.map((s) => (
            <div key={s.id} className="flex items-center justify-between px-4 py-3 flex-wrap gap-3" data-testid={`sender-row-${s.id}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-white font-medium truncate">{s.from_name}</span>
                  {s.verified ? (
                    <span className="cf-badge cf-badge-success flex items-center gap-1"><CheckCircle2 size={10} /> verified</span>
                  ) : (
                    <span className="cf-badge cf-badge-warn flex items-center gap-1"><Clock size={10} /> pending</span>
                  )}
                </div>
                <div className="text-zinc-500 font-mono text-xs truncate">{s.from_email}</div>
              </div>
              <div className="flex items-center gap-2">
                {s.verified && onPickSender && (
                  <button onClick={() => onPickSender(s.from_email, s.from_name)} data-testid={`use-sender-${s.id}`} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                    Use as my sender
                  </button>
                )}
                {!s.verified && (
                  <button onClick={() => onResend(s.id)} data-testid={`resend-${s.id}`} className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
                    Resend email
                  </button>
                )}
                {!s.locked && (
                  <button onClick={() => onDelete(s.id, s.from_email)} data-testid={`delete-sender-${s.id}`} className="text-zinc-600 hover:text-[#E23A28]">
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
